import { Fragment } from "react";
import moment from "moment";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowRight01Icon,
  ArrowRight02Icon,
  DashedLineCircleIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { CheckmarkCircle02Icon } from "@hugeicons-pro/core-solid-rounded";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { cx } from "shared/utils";
import { OnboardingChecklistItem } from "api/onboarding-checklist";
import {
  ONBOARDING_CHECKLIST_COPY,
  ONBOARDING_CHECKLIST_CTA_LABEL
} from "../constants";

const COMPLETION_DATE_FORMAT = "MMM D [at] h:mmA";

// Account/property-level items complete without a teammate to name, and the design
// still shows a completion line for them — just the timestamp.
const getCompletionLine = (item: OnboardingChecklistItem) => {
  if (!item.completed_at) return null;

  const completedAt = moment(item.completed_at).format(COMPLETION_DATE_FORMAT);

  return item.completed_by
    ? `Completed by ${item.completed_by.name} on ${completedAt}`
    : `Completed on ${completedAt}`;
};

const Breadcrumb = ({ segments }: { segments: string[] }) => (
  <p className="flex items-center gap-2.5 text-sm font-medium text-content-300">
    {segments.map((segment, index) => (
      <Fragment key={segment}>
        {index > 0 && (
          <HugeiconsIcon
            icon={ArrowRight01Icon}
            size={20}
            className="shrink-0"
          />
        )}
        <span>{segment}</span>
      </Fragment>
    ))}
  </p>
);

export const ChecklistItem = ({
  item,
  onManage
}: {
  item: OnboardingChecklistItem;
  onManage: (route: string) => void;
}) => {
  const copy = ONBOARDING_CHECKLIST_COPY[item.key];
  const completionLine = getCompletionLine(item);

  return (
    <li className="flex items-start gap-4 py-4">
      {item.complete ? (
        <HugeiconsIcon
          icon={CheckmarkCircle02Icon}
          size={24}
          className="mt-px shrink-0 text-primary-500"
        />
      ) : (
        <HugeiconsIcon
          icon={DashedLineCircleIcon}
          size={24}
          className="mt-px shrink-0 text-content-100"
        />
      )}
      <div className="min-w-0 flex-1 space-y-2">
        <p
          className={cx(
            "text-sm font-bold",
            item.complete ? "text-content-200" : "text-content-1000"
          )}
        >
          {item.label}
        </p>
        <Show if={copy}>
          <p
            className={cx(
              "text-sm font-medium",
              item.complete ? "text-content-200" : "text-content-300"
            )}
          >
            {copy?.description}
          </p>
        </Show>
        {item.complete ? (
          <Show if={completionLine}>
            <p className="text-sm font-medium text-content-200">
              {completionLine}
            </p>
          </Show>
        ) : (
          <div className="flex items-center gap-4 pt-1">
            <Show if={copy?.breadcrumb}>
              <Breadcrumb segments={copy?.breadcrumb ?? []} />
            </Show>
            <Button
              className="ml-auto"
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.XS}
              HugeIcon={ArrowRight02Icon}
              iconPosition="right"
              onClick={() => onManage(item.cta_route)}
            >
              {ONBOARDING_CHECKLIST_CTA_LABEL}
            </Button>
          </div>
        )}
      </div>
    </li>
  );
};
